import { useState } from 'react';
import { SimpleGrid, Button, Card, Loader, Center, Modal, Stack, TextInput, Group, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { TableToolbar } from '../components/TableToolbar';
import { IconPlus, IconChevronRight, IconBook } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { SubjectIconPicker, SubjectIconView } from '../components/SubjectIcon';

interface Subject {
  id: string;
  name: string;
  code?: string;
  /** Admin TANLAGAN ikonka kaliti (null — tanlanmagan) */
  icon?: string | null;
  /** Backend fan nomiga qarab TAKLIF qilgan kalit (DB'ga yozilmagan, doim to'ldirilgan) */
  suggestedIcon?: string;
}

export default function Subjects() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [sort, setSort] = useState('name:asc');
  const { data: subjects, isLoading } = useQuery({
    queryKey: ['subjects-page', qDeb, sort],
    queryFn: async () => (await api.get<Subject[]>('/subjects', { params: { q: qDeb || undefined, sort } })).data,
  });

  const form = useForm({
    // `icon: null` — standart holat: fan nomiga qarab TAKLIF ishlatiladi va DB'ga
    // hech nima yozilmaydi (kontrakt: taklifni faqat admin tanlasa saqlaymiz).
    initialValues: { name: '', code: '', nameRu: '', nameEn: '', icon: null as string | null },
    validate: { name: (v) => (!v ? t('qc.subjectNameRequired') : null) },
  });

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/subjects', { ...v, icon: v.icon ?? undefined }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('qc.subjectAdded') }); qc.invalidateQueries({ queryKey: ['subjects'] }); qc.invalidateQueries({ queryKey: ['subjects-page'] }); form.reset(); close(); },
  });

  return (
    <>
      <PageHeader title={t('pages.subjects.title')} subtitle={`${t('common.total')}: ${subjects?.length ?? 0}`} action={<Button leftSection={<IconPlus size={18} />} onClick={open}>{t('pages.subjects.add')}</Button>} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('subjectsPage.searchPlaceholder')}
        sort={sort}
        onSort={setSort}
        sortOptions={[
          { value: 'name:asc', label: t('subjectsPage.sortNameAsc') },
          { value: 'name:desc', label: t('subjectsPage.sortNameDesc') },
          { value: 'createdAt:desc', label: t('subjectsPage.sortNewest') },
        ]}
      />
      {/* Karta doim ko'rinadi — ichida yuklanish/bo'sh holat/ro'yxat almashadi */}
      <SectionCard title={t('common.list')} icon={IconBook}>
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !subjects?.length ? (
          <Center h={140}><Text c="dimmed">{t('common.noMatch')}</Text></Center>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
            {subjects.map((s) => (
              <Card key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/subjects/${s.id}`)}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    {/* Ko'rinadigan ikonka = tanlangani, bo'lmasa nomga qarab taklif */}
                    <SubjectIconView icon={s.icon} suggested={s.suggestedIcon} />
                    <Text fw={500} lineClamp={1}>{s.name}</Text>
                  </Group>
                  <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </SectionCard>

      <Modal opened={opened} onClose={close} title={t('pages.subjects.add')} centered>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <TextInput label={t('subjectsPage.nameUz')} placeholder="Matematika" required {...form.getInputProps('name')} />
            <TextInput label={t('subjectsPage.nameRu')} placeholder="Математика" {...form.getInputProps('nameRu')} />
            <TextInput label={t('subjectsPage.nameEn')} placeholder="Mathematics" {...form.getInputProps('nameEn')} />
            <TextInput label={t('qc.code')} placeholder="MATH" {...form.getInputProps('code')} />
            {/* Taklif serverdan keladi (fan hali yaratilmagani uchun bu yerda yo'q) —
                shu sabab yangi fanda "standart" tugmasi neytral holatda turadi. */}
            <SubjectIconPicker value={form.values.icon} onChange={(v) => form.setFieldValue('icon', v)} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
